import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { computePsychograph } from '../engine/psychograph';
import { generateLifestyleGuidance } from '../engine/lifestyle';
import { getDietSuggestions, getMovementSuggestions } from '../engine/recommendations';

// ─── Styles ────────────────────────────────────────────────────────────────

const s = {
  page: { paddingTop: '64px', background: 'var(--cream)', minHeight: '100vh' },

  hero: {
    background: 'var(--navy)', color: 'white',
    padding: '64px 0 56px',
  },
  heroInner: { maxWidth: '800px', margin: '0 auto', padding: '0 48px' },
  heroEyebrow: {
    fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.18em',
    textTransform: 'uppercase', color: 'var(--amber-light)',
    marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px',
  },
  eyebrowLine: { display: 'block', width: '24px', height: '1px', background: 'var(--amber-light)' },
  heroTitle: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 600, lineHeight: 1.1, marginBottom: '16px',
  },
  heroSub: {
    fontSize: '0.95rem', fontWeight: 300, opacity: 0.75, lineHeight: 1.7,
    maxWidth: '560px', marginBottom: '32px',
  },
  heroMeta: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  heroPill: {
    fontSize: '0.78rem', fontWeight: 500,
    background: 'rgba(255,255,255,0.12)',
    padding: '6px 14px', borderRadius: '999px',
    display: 'flex', alignItems: 'center', gap: '6px',
  },

  // Strengths strip
  strengthsStrip: {
    marginTop: '28px', paddingTop: '24px',
    borderTop: '1px solid rgba(255,255,255,0.15)',
  },
  strengthsLabel: {
    fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber-light)', marginBottom: '10px',
  },
  // Voiced kudos intro — readable sentence, not uppercase label
  kudosIntro: {
    fontSize: '0.9rem', fontWeight: 300, color: 'rgba(255,255,255,0.75)',
    lineHeight: 1.6, marginBottom: '12px', fontStyle: 'italic',
  },
  // 0-strength encouragement strip
  encourageStrip: {
    marginTop: '28px', paddingTop: '24px',
    borderTop: '1px solid rgba(255,255,255,0.15)',
  },
  encourageText: {
    fontSize: '0.9rem', fontWeight: 300, color: 'rgba(255,255,255,0.72)',
    lineHeight: 1.7, fontStyle: 'italic',
  },
  strengthsPills: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  strengthPill: {
    fontSize: '0.78rem', fontWeight: 400,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.20)',
    padding: '5px 13px', borderRadius: '999px',
    color: 'rgba(255,255,255,0.88)',
  },

  // Source bar
  sourceBar: {
    background: '#EFF6FF', borderBottom: '1px solid #DBEAFE', padding: '20px 0',
  },
  sourceInner: {
    maxWidth: '800px', margin: '0 auto', padding: '0 48px',
    display: 'flex', gap: '16px', alignItems: 'flex-start',
  },
  sourceIcon: { fontSize: '1.2rem', flexShrink: 0, marginTop: '2px' },
  sourceText: {
    fontSize: '0.83rem', color: '#1E40AF', lineHeight: 1.7, fontWeight: 300,
  },

  // Main
  main: { maxWidth: '800px', margin: '0 auto', padding: '48px 48px 80px' },

  sectionLabel: {
    fontSize: '0.68rem', fontWeight: 500, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber)',
    marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  sectionLabelLine: { display: 'block', width: '20px', height: '1px', background: 'var(--amber)' },
  sectionTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.6rem',
    fontWeight: 600, color: 'var(--navy)', marginBottom: '6px',
  },
  sectionDesc: {
    fontSize: '0.88rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.7, marginBottom: '24px',
  },

  productCard: {
    background: 'white', borderRadius: '12px',
    border: '1px solid var(--border)', marginBottom: '14px', overflow: 'hidden',
  },
  productHeader: (expanded) => ({
    padding: '20px 24px', cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    background: expanded ? '#F8FAFC' : 'white', transition: 'background 0.15s',
  }),
  productLeft: { flex: 1 },
  productName: { fontSize: '1rem', fontWeight: 500, color: 'var(--navy)', marginBottom: '4px' },
  productMeta: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  productServing: { fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 300 },
  productItem: {
    fontSize: '0.72rem', color: 'var(--muted)',
    background: 'var(--phase-bg)', padding: '2px 8px', borderRadius: '4px',
  },
  tierBadge: (tier) => ({
    fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em',
    textTransform: 'uppercase', padding: '3px 10px', borderRadius: '999px',
    background: tier === 1 ? '#EFF6FF' : tier === 2 ? '#FFFBEB' : '#F9FAFB',
    color: tier === 1 ? 'var(--navy)' : tier === 2 ? 'var(--amber)' : 'var(--muted)',
    border: `1px solid ${tier === 1 ? '#DBEAFE' : tier === 2 ? '#FDE68A' : 'var(--border)'}`,
  }),
  chevron: (expanded) => ({
    fontSize: '0.8rem', color: 'var(--muted)', transition: 'transform 0.2s',
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    marginLeft: '16px', flexShrink: 0,
  }),
  productBody: { padding: '0 24px 20px', borderTop: '1px solid var(--border)' },
  productWhyPreview: {
    fontSize: '0.82rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.6, marginTop: '6px', marginBottom: '2px',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
    productWhy: {
    fontSize: '0.88rem', color: '#374151', lineHeight: 1.75,
    fontWeight: 300, marginTop: '16px', marginBottom: '12px',
  },
  productScience: {
    fontSize: '0.8rem', color: '#4B5563', lineHeight: 1.7,
    fontWeight: 300, padding: '12px 16px',
    background: '#F0F4F8', borderRadius: '8px', borderLeft: '3px solid var(--navy)',
  },
  productNote: {
    fontSize: '0.78rem', color: '#92400E', padding: '10px 14px',
    background: '#FFFBEB', borderRadius: '6px', marginTop: '10px',
    borderLeft: '3px solid var(--amber-light)',
  },

  tierSection: { marginBottom: '48px' },
  tierHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid var(--border)',
  },
  tierIcon: { fontSize: '1.2rem' },
  tierLabel: { fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 600, color: 'var(--navy)' },
  tierCount: {
    fontSize: '0.75rem', color: 'var(--muted)',
    background: 'var(--phase-bg)', padding: '3px 10px', borderRadius: '999px',
  },
  tierRationale: {
    fontSize: '0.85rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.7, marginBottom: '20px',
  },

  moduleSection: {
    background: 'white', borderRadius: '14px',
    border: '1px solid var(--border)', marginBottom: '20px', overflow: 'hidden',
  },
  moduleHeader: (expanded) => ({
    padding: '20px 24px', cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: expanded ? '#F8FAFC' : 'white',
  }),
  moduleHeaderLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  moduleIcon: { fontSize: '1.4rem' },
  moduleTitle: { fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 600, color: 'var(--navy)' },
  moduleBody: { padding: '0 24px 24px', borderTop: '1px solid var(--border)' },
  moduleRationale: {
    fontSize: '0.88rem', fontWeight: 300, color: '#4B5563',
    lineHeight: 1.75, marginTop: '16px', marginBottom: '20px',
  },

  guidanceBox: {
    background: 'white', borderRadius: '12px',
    border: '1px solid var(--border)', padding: '28px', marginBottom: '48px',
  },
  guidanceItem: {
    fontSize: '0.88rem', fontWeight: 300, color: '#374151',
    lineHeight: 1.75, marginBottom: '12px',
    paddingLeft: '20px', position: 'relative',
  },

  dietBox: {
    background: 'white', borderRadius: '12px',
    border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '48px',
  },
  dietHeader: {
    padding: '16px 24px', background: '#F0FDF4',
    borderBottom: '1px solid #BBF7D0',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  dietHeaderIcon: { fontSize: '1rem' },
  dietHeaderText: {
    fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#166534',
  },
  dietItem: {
    fontSize: '0.88rem', fontWeight: 300, color: '#374151',
    lineHeight: 1.75, padding: '16px 24px',
    borderBottom: '1px solid var(--border)',
    position: 'relative', paddingLeft: '44px',
  },
  dietBullet: {
    position: 'absolute', left: '22px', top: '18px',
    color: '#16A34A', fontWeight: 700, fontSize: '0.85rem',
  },

  coachCTA: {
    background: 'var(--navy)', borderRadius: '16px',
    padding: '40px', marginBottom: '48px',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', gap: '32px', flexWrap: 'wrap',
  },
  coachLeft: {},
  coachTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.5rem',
    fontWeight: 600, color: 'white', marginBottom: '8px',
  },
  coachDesc: {
    fontSize: '0.88rem', fontWeight: 300, color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.7, maxWidth: '440px',
  },
  coachBtn: {
    background: 'var(--amber)', color: 'white', padding: '14px 32px',
    borderRadius: '8px', fontSize: '0.9rem', fontWeight: 500,
    border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.02em',
  },


  // Lumen Engage Card
  checkinCard: {
    background: 'var(--slate)', borderRadius: '14px',
    border: '1px solid var(--border)',
    padding: '28px 32px',
    marginTop: '16px', marginBottom: '32px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '24px',
  },
  checkinText: {
    fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.6, flex: 1,
  },
  checkinBtn: {
    display: 'inline-block', padding: '12px 24px', borderRadius: '8px',
    background: 'var(--navy)', color: 'white',
    fontSize: '0.88rem', fontWeight: 500, textDecoration: 'none',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
    engageCard: {
    background: 'white',
    border: '2px solid var(--amber)',
    borderRadius: '16px',
    padding: '36px 40px',
    marginBottom: '48px',
  },
  engageEyebrow: {
    fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber)',
    marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  engageEyebrowLine: { display: 'block', width: '20px', height: '1px', background: 'var(--amber)', flexShrink: 0 },
  engageTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.5rem',
    fontWeight: 600, color: 'var(--navy)', marginBottom: '10px',
  },
  engageDesc: {
    fontSize: '0.88rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.7, marginBottom: '24px',
  },
  engageQuestions: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' },
  engageQuestion: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '14px 18px',
    background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px',
    fontSize: '0.88rem', color: 'var(--navy)', fontWeight: 400,
    lineHeight: 1.55, textDecoration: 'none', cursor: 'pointer',
  },
  engageQuestionArrow: { color: 'var(--amber)', fontWeight: 700, flexShrink: 0, marginTop: '1px' },
  engageBtn: {
    background: 'var(--amber)', color: 'white', padding: '14px 32px',
    borderRadius: '8px', fontSize: '0.9rem', fontWeight: 500,
    border: 'none', cursor: 'pointer', letterSpacing: '0.02em',
  },

  // Preamble card
  preambleCard: {
    marginTop: '32px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '14px',
    padding: '28px 32px',
  },
  preambleHeadline: {
    fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber-light)',
    marginBottom: '12px',
  },
  preambleBody: {
    fontSize: '0.92rem', fontWeight: 300, color: 'rgba(255,255,255,0.85)',
    lineHeight: 1.8, marginBottom: '16px',
  },

  budgetBanner: {
    background: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderLeft: '4px solid var(--amber)',
    borderRadius: '12px',
    padding: '24px 28px',
    marginBottom: '32px',
  },
  budgetBannerEyebrow: {
    fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber)',
    marginBottom: '8px',
  },
  budgetBannerTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.1rem',
    fontWeight: 600, color: 'var(--navy)', marginBottom: '10px',
  },
  budgetBannerBody: {
    fontSize: '0.88rem', fontWeight: 300, color: '#374151',
    lineHeight: 1.75, marginBottom: '14px',
  },
  budgetBannerPhase: {
    fontSize: '0.82rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.65, fontStyle: 'italic',
  },
  practiceCard: {
    background: 'white', borderRadius: '14px',
    border: '1px solid var(--border)', padding: '28px 32px',
    marginBottom: '32px',
  },
  openCard: {
    background: '#F8FAFC', borderRadius: '14px',
    border: '1px solid var(--border)', padding: '28px 32px',
    marginBottom: '32px',
  },
  practiceEyebrow: {
    fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'var(--amber)',
    marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px',
  },
  practiceEyebrowLine: { display: 'block', width: '20px', height: '1px', background: 'var(--amber)', flexShrink: 0 },
  practiceTitle: {
    fontFamily: 'var(--font-display)', fontSize: '1.15rem',
    fontWeight: 600, color: 'var(--navy)', marginBottom: '8px',
  },
  practiceOpener: {
    fontSize: '0.88rem', fontWeight: 300, color: 'var(--muted)',
    lineHeight: 1.7, marginBottom: '16px', fontStyle: 'italic',
  },
  practiceSuggestions: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  practiceSuggestion: {
    fontSize: '0.85rem', fontWeight: 300, color: '#374151',
    lineHeight: 1.75, padding: '14px 18px',
    background: '#F8FAFC', border: '1px solid var(--border)',
    borderRadius: '10px', borderLeft: '3px solid var(--amber)',
  },
  practiceCheckbox: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '0.88rem', color: 'var(--navy)', fontWeight: 400,
    cursor: 'pointer', padding: '12px 0',
    borderTop: '1px solid var(--border)', marginTop: '4px',
    userSelect: 'none',
  },
  compliance: {
    padding: '24px', background: '#FFFBEB', borderRadius: '10px', marginTop: '24px',
    fontSize: '0.76rem', color: '#92400E', lineHeight: 1.7,
  },
};

// ─── Compute strengths from assessment answers ─────────────────────────────

function computeStrengths(a) {
  const strengths = [];
  const ex = a.q13_exercise || '';
  const exType = Array.isArray(a.q14_exercise_type) ? a.q14_exercise_type : [];
  const strength = a.q_strength || '';
  const sleep = a.q15_sleep || '';
  const stress = a.q16_stress || '';
  const diet = a.q5_diet_quality || '';
  const veg = a.q6_vegetables || '';
  const fish = a.q8_fish || '';
  const sun = a.q11_sun || '';
  const mindbody = Array.isArray(a.q_mindbody) ? a.q_mindbody : [];
  const nature = a.q_nature || '';
  const civic = a.q_civic || '';

  // Cardiovascular exercise
  if (ex.startsWith('Very active') || ex.startsWith('Active')) {
    strengths.push('Regular cardiovascular exercise');
  }
  // Resistance training
  if (strength.includes('Twice') || strength.includes('Three') || exType.some(e => e.includes('Strength'))) {
    strengths.push('Resistance training');
  }
  // Sleep quality
  if (sleep.startsWith('Excellent') || sleep.startsWith('Good')) {
    strengths.push('Sleep quality & duration');
  }
  // Stress management
  if (stress.startsWith('Low')) {
    strengths.push('Stress management');
  }
  // Diet quality
  if (diet.startsWith('Excellent') || diet.startsWith('Good')) {
    strengths.push('Diet quality');
  }
  // Vegetable intake
  if (veg === '5 or more' || veg === '3–4') {
    strengths.push('Vegetable intake');
  }
  // Fish intake
  if (fish.startsWith('3+') || fish.startsWith('1–2')) {
    strengths.push('Omega-3 from whole food sources');
  }
  // Sun / vitamin D
  if (sun.startsWith('Regular')) {
    strengths.push('Daily sun exposure');
  }
  // Mind-body practice
  if (mindbody.some(m => !m.includes('None'))) {
    strengths.push('Mind-body practice');
  }
  // Nature contact
  if (nature.startsWith("Yes")) {
    strengths.push('Regular nature contact');
  }
  // Creative practice
  const arts = a.q_arts || '';
  if (arts.startsWith('Yes')) {
    strengths.push('Active creative practice');
  }
  // Community / civic
  if (civic.startsWith('Yes')) {
    strengths.push('Community & civic engagement');
  }
  return strengths;
}

function generatePreamble(psychograph, protocol, assessmentAnswers) {
  if (!psychograph || !protocol) return null;

  const voice = psychograph.voice;
  const a = assessmentAnswers || {};
  const foundationalCount = (protocol.foundational || []).length;
  const moduleCount = (protocol.modules || []).length;
  const totalRecs = foundationalCount + (protocol.modules || []).reduce((acc, m) => acc + (m.products?.length || 0), 0);
  const name = a.name ? a.name.split(' ')[0] : null;
  const greeting = name ? `${name}, what` : 'What';

  if (voice === 'ANALYST') {
    return {
      headline: 'What the data built',
      body: `${greeting} follows is a protocol assembled from your specific inputs — not a template, not a generic starting point. Your assessment surfaced ${moduleCount} targeted area${moduleCount !== 1 ? 's' : ''} worth addressing alongside ${foundationalCount} foundation product${foundationalCount !== 1 ? 's' : ''} that apply regardless of your goals. Every recommendation traces back to something you described. The rationale and science are visible inside each card — tap any product to see exactly why it's here. Once you've read through, Lumen can go deeper on any piece of this: the mechanism, the sequencing, where to start, what to prioritize.`,
    };
  }

  if (voice === 'SEEKER') {
    return {
      headline: 'A picture of where you are',
      body: `This protocol reflects the whole picture — the practices you've already built, the patterns worth strengthening, the gaps that showed up in your assessment. ${totalRecs} recommendations emerged from what you described about your life: your energy, your sleep, your stress, your movement, your goals. Nothing here is incidental. What follows is an invitation to understand yourself a little more clearly — and to build from there. Lumen has read your full protocol and can help you make sense of any part of it, or think through how it fits into the life you're building.`,
    };
  }

  // PRAGMATIST
  const isBudgetMin = (a.q26_budget || '').startsWith('Minimal');
  if (isBudgetMin) {
    return {
      headline: "Built from your answers — starting where you are",
      body: `You answered honestly, so this was built specifically for you — including your budget. Everything below is your complete roadmap. Your starting point is clearly marked. Lumen can help you sequence the rest when you're ready.`,
    };
  }
  return {
    headline: "Built from your answers — here's what to do",
    body: `You answered honestly, so this was built specifically for you. ${totalRecs} recommendations: ${foundationalCount} non-negotiable foundation product${foundationalCount !== 1 ? 's' : ''} to start with, and ${moduleCount} targeted module${moduleCount !== 1 ? 's' : ''} based on your priorities. Start with Foundation. Add Targeted over time. Each card below shows what it is, why it's here, and the science behind it. When you're ready to build a routine or figure out what to prioritize first, Lumen can help you do that in about two minutes.`,
  };
}



// ─── Product card ──────────────────────────────────────────────────────────

function ProductCard({ product }) {
  const [expanded, setExpanded] = useState(false);
  const tierLabel = product.tier === 1 ? 'Foundation' : product.tier === 2 ? 'Targeted' : 'Optional';

  return (
    <div style={s.productCard}>
      <div style={s.productHeader(expanded)} onClick={() => setExpanded(!expanded)}>
        <div style={s.productLeft}>
          <div style={s.productName}>{product.name}</div>
          <div style={s.productMeta}>
            <span style={s.tierBadge(product.tier)}>{tierLabel}</span>
            <span style={s.productServing}>{product.serving}</span>
            {product.item && <span style={s.productItem}>{product.item}</span>}
          </div>
        </div>
        <span style={s.chevron(expanded)}>▼</span>
      </div>
      {product.why && !expanded && (
        <div style={{ padding: '0 20px 14px' }}>
          <p style={s.productWhyPreview}>{product.why}</p>
        </div>
      )}
      {expanded && (
        <div style={s.productBody}>
          <p style={s.productWhy}>{product.why}</p>
          {product.science && (
            <div style={s.productScience}>
              <strong style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--navy)' }}>Science context </strong>
              {product.science}
            </div>
          )}
          {product.note && <div style={s.productNote}>⚠ {product.note}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Module section ────────────────────────────────────────────────────────

function ModuleSection({ module }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={s.moduleSection}>
      <div style={s.moduleHeader(expanded)} onClick={() => setExpanded(!expanded)}>
        <div style={s.moduleHeaderLeft}>
          <span style={s.moduleIcon}>{module.icon}</span>
          <span style={s.moduleTitle}>{module.title}</span>
        </div>
        <span style={s.chevron(expanded)}>▼</span>
      </div>
      {expanded && (
        <div style={s.moduleBody}>
          <p style={s.moduleRationale}>{module.rationale}</p>
          {(module.products || []).map(p => <ProductCard key={p.name} product={p} />)}
        </div>
      )}
    </div>
  );
}

// ─── Main Results component ────────────────────────────────────────────────

export default function Results({ protocol, lifestyleGuidance: lifestyleGuidanceProp, assessmentAnswers }) {
  const [dietOptIn, setDietOptIn] = React.useState(false);
  const [movementOptIn, setMovementOptIn] = React.useState(false);
  const [openOptIn, setOpenOptIn] = React.useState(false);
  const dietSuggestions     = getDietSuggestions(assessmentAnswers);
  const movementSuggestions = getMovementSuggestions(assessmentAnswers);

    if (protocol?.error) {
    return (
      <div style={{ padding: '80px 48px', maxWidth: '680px', margin: '0 auto' }}>
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', padding: '32px' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#DC2626', marginBottom: '12px' }}>ENGINE ERROR — PLEASE COPY AND REPORT</div>
          <pre style={{ fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{protocol.error}</pre>
          <div style={{ marginTop: '24px' }}><a href="/assessment" style={{ color: 'var(--navy)', fontWeight: 500 }}>← Retake assessment</a></div>
        </div>
      </div>
    );
  }

  if (!protocol) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: 'var(--muted)', marginBottom: '16px' }}>No protocol found.</p>
          <Link to="/assessment" onClick={() => { try { localStorage.removeItem('lih_session'); } catch {} }} style={{ color: 'var(--navy)', fontWeight: 500 }}>← Retake assessment</Link>
        </div>
      </div>
    );
  }

  // ── Psychograph & voice-modulated guidance ─────────────────────────────
  let psychograph = null;
  let rawGuidance = lifestyleGuidanceProp || '';
  try {
    psychograph = assessmentAnswers ? computePsychograph(assessmentAnswers) : null;
    if (assessmentAnswers && psychograph) {
      rawGuidance = generateLifestyleGuidance(assessmentAnswers, psychograph);
    }
  } catch (err) {
    console.error('[LIH] Results render error:', err);
  }

  const allItems  = rawGuidance.split('\n\n').filter(Boolean);
  const guidance  = allItems.filter(item => !item.startsWith('DIET:'));
  const dietItems = allItems.filter(item => item.startsWith('DIET:')).map(item => item.replace(/^DIET:\s*/, ''));

  const foundational = protocol.foundational || [];
  const modules       = protocol.modules || [];
  const strengths     = computeStrengths(assessmentAnswers);

  return (
    <div style={s.page}>

      {/* ── Hero ── */}
      <div style={s.hero}>
        <div style={s.heroInner}>
          <div style={s.heroEyebrow}>
            <span style={s.eyebrowLine} />
            Your Personal Protocol
          </div>
          <h1 style={s.heroTitle}>
            Built from your answers —<br />
            here’s what we found.
          </h1>
          <p style={s.heroSub}>
            Everything below was generated from your specific assessment: your habits, your food,
            your goals, your starting point. The coaching layer — we call it Lumen — is where
            this becomes specific to your actual life. It’s the highest-value part of the tool.
          </p>
          <div style={s.heroMeta}>
            {(assessmentAnswers?.q26_budget || '').startsWith('Minimal') ? (
              <>
                <span style={s.heroPill}>✓ Starting point identified</span>
                <span style={s.heroPill}>✓ Complete roadmap included</span>
                {modules.length > 0 && (
                  <span style={s.heroPill}>✓ {modules.length} targeted module{modules.length !== 1 ? 's' : ''}</span>
                )}
                {guidance.length > 0 && <span style={s.heroPill}>✓ Lifestyle guidance included</span>}
              </>
            ) : (
              <>
                <span style={s.heroPill}>✓ {foundational.length} foundation product{foundational.length !== 1 ? 's' : ''}</span>
                {modules.length > 0 && (
                  <span style={s.heroPill}>✓ {modules.length} targeted module{modules.length !== 1 ? 's' : ''}</span>
                )}
                {guidance.length > 0 && <span style={s.heroPill}>✓ Lifestyle guidance included</span>}
              </>
            )}
          </div>

          {(() => {
            const kudosVoice = psychograph?.voice || 'PRAGMATIST';
            if (strengths.length >= 2) {
              const intro = {
                ANALYST: 'Your data shows these foundations already in place:',
                SEEKER: "You're already living these practices — this protocol builds on them:",
                PRAGMATIST: "You've got these locked in. The protocol fills the gaps:",
              }[kudosVoice];
              return (
                <div style={s.strengthsStrip}>
                  <div style={s.kudosIntro}>{intro}</div>
                  <div style={s.strengthsPills}>
                    {strengths.map((strength, i) => (
                      <span key={i} style={s.strengthPill}>{strength}</span>
                    ))}
                  </div>
                </div>
              );
            }
            if (strengths.length === 1) {
              const intro = {
                ANALYST: 'One strong data point to build from:',
                SEEKER: 'One practice already in place — a real foundation:',
                PRAGMATIST: 'One locked in. More to build:',
              }[kudosVoice];
              return (
                <div style={s.strengthsStrip}>
                  <div style={s.kudosIntro}>{intro}</div>
                  <div style={s.strengthsPills}>
                    <span style={s.strengthPill}>{strengths[0]}</span>
                  </div>
                </div>
              );
            }
            // 0 strengths — honest, warm, voice-specific encouragement
            const encouragement = {
              ANALYST: "Your assessment didn't flag any established lifestyle habits — which means the protocol has more to do. That's actually a more complete picture of what's possible with some focused effort.",
              SEEKER: "Your assessment shows you're at a starting point rather than building on existing habits. That's not a judgment — it's useful information. The protocol meets you where you are.",
              PRAGMATIST: "No established habits yet — the protocol is doing more work for you than it would for someone starting from a stronger foundation. That's fine. Start with Foundation and build from there.",
            }[kudosVoice];
            return (
              <div style={s.encourageStrip}>
                <p style={s.encourageText}>{encouragement}</p>
              </div>
            );
          })()}

        {/* ── Check In ── */}
        {(() => {
          const LIH_HISTORY_KEY = 'lih_coaching_history';
          let hasHistory = false;
          try {
            const stored = localStorage.getItem(LIH_HISTORY_KEY);
            hasHistory = stored && JSON.parse(stored).length > 0;
          } catch {}
          if (!hasHistory) return null;
          return (
            <div style={s.checkinCard}>
              <p style={s.checkinText}>
                You have a previous conversation with Lumen. Check in to pick up where you left off —
                what\'s changed, what\'s working, what to adjust.
              </p>
              <Link to="/coach" state={{ mode: 'checkin' }} style={s.checkinBtn}>
                Check in with Lumen →
              </Link>
            </div>
          );
        })()}


          {/* ── Preamble ── */}
          {(() => {
            const preamble = generatePreamble(psychograph, protocol, assessmentAnswers);
            return preamble ? (
              <div style={s.preambleCard}>
                <div style={s.preambleHeadline}>{preamble.headline}</div>
                <p style={s.preambleBody}>{preamble.body}</p>
              </div>
            ) : null;
          })()}

        </div>
      </div>

      {/* ── Source narrative ── */}
      <div style={s.sourceBar}>
        <div style={s.sourceInner}>
          <div>
            <p style={s.sourceText}>
              This tool was built by Bob Ferguson to share what he has learned about what actually creates lasting health — the habits, the science, and the right nutritional foundation for where you are right now.
            </p>
          </div>
        </div>
      </div>

      <div style={s.main}>

        {/* ── ACT 1: Your Habits ── */}
        {(guidance.length > 0 || movementSuggestions.length > 0) && (
          <div style={s.tierSection}>
            <div style={{ ...s.sectionLabel, marginBottom: '6px' }}>
              <span style={s.sectionLabelLine} />
              Your Habits
            </div>
            <h2 style={s.sectionTitle}>The practices that make everything else work better</h2>
            <p style={s.sectionDesc}>
              These habits address the conditions that determine how well everything else works.
              Every observation below is drawn directly from what you described in your assessment.
            </p>
            {guidance.length > 0 && (
              <div style={s.guidanceBox}>
                {guidance.map((item, i) => (
                  <div key={i} style={s.guidanceItem}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--amber)', fontWeight: 700 }}>→</span>
                    {item}
                  </div>
                ))}
              </div>
            )}
            {movementSuggestions.length > 0 && (
              <div style={{ ...s.practiceCard, marginTop: guidance.length > 0 ? '0' : '0', border: '1px solid var(--border)', background: '#FAFBFC' }}>
                <div style={s.practiceEyebrow}>
                  <span style={s.practiceEyebrowLine} />
                  Your Movement Practice
                </div>
                <h3 style={s.practiceTitle}>Where focused movement effort pays off most</h3>
                <p style={s.practiceOpener}>
                  {(assessmentAnswers?.q13_exercise || '').startsWith('Sedentary')
                    ? "Your assessment shows little regular movement — these are the most effective first steps, not the most ambitious ones."
                    : (assessmentAnswers?.q13_exercise || '').startsWith('Lightly active')
                      ? "You're moving occasionally — these suggestions are about building the habit architecture around what you're already doing."
                      : (assessmentAnswers?.q13_exercise || '').startsWith('Moderately active')
                        ? "You have a movement foundation. These are the next leverage points specific to your profile."
                        : "You're training consistently — what follows isn't about doing more, it's about doing it more intelligently."}
                </p>
                <div style={s.practiceSuggestions}>
                  {movementSuggestions.map((sg, i) => (
                    <div key={i} style={s.practiceSuggestion}>{sg.text}</div>
                  ))}
                </div>
                <label style={s.practiceCheckbox}>
                  <input
                    type="checkbox"
                    checked={movementOptIn}
                    onChange={e => setMovementOptIn(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--amber)', flexShrink: 0 }}
                  />
                  I’d like to explore my movement practice with Lumen
                </label>
              </div>
            )}
          </div>
        )}

        {/* ── ACT 2: Your Food Practice ── */}
        {(dietItems.length > 0 || dietSuggestions.length > 0) && (
          <div style={s.tierSection}>
            <div style={{ ...s.sectionLabel, marginBottom: '6px' }}>
              <span style={s.sectionLabelLine} />
              Your Food Practice
            </div>
            <h2 style={s.sectionTitle}>Food first — dietary priorities for your profile</h2>
            <p style={s.sectionDesc}>
              These priorities are drawn directly from what you described, and they’re where
              the supplement protocol does its best work.
            </p>
            {dietItems.length > 0 && (
              <div style={s.dietBox}>
                <div style={s.dietHeader}>
                  <span style={s.dietHeaderIcon}>🥗</span>
                  <span style={s.dietHeaderText}>Dietary priorities — specific to your profile</span>
                </div>
                {dietItems.map((item, i) => (
                  <div key={i} style={{
                    ...s.dietItem,
                    borderBottom: i < dietItems.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <span style={s.dietBullet}>→</span>
                    {item}
                  </div>
                ))}
              </div>
            )}
            {dietSuggestions.length > 0 && (
              <div style={{ ...s.practiceCard, marginTop: '0', border: '1px solid var(--border)', background: '#FAFBFC' }}>
                <div style={s.practiceEyebrow}>
                  <span style={s.practiceEyebrowLine} />
                  Starting points
                </div>
                <h3 style={s.practiceTitle}>
                  {((assessmentAnswers?.q5_diet_quality || '').startsWith('Poor') ||
                    (assessmentAnswers?.q5_diet_quality || '').startsWith('Mixed'))
                    ? "Highest-leverage changes for your profile"
                    : "Specific gaps worth closing"}
                </h3>
                <div style={s.practiceSuggestions}>
                  {dietSuggestions.map((sg, i) => (
                    <div key={i} style={s.practiceSuggestion}>{sg.text}</div>
                  ))}
                </div>
                <label style={s.practiceCheckbox}>
                  <input
                    type="checkbox"
                    checked={dietOptIn}
                    onChange={e => setDietOptIn(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--amber)', flexShrink: 0 }}
                  />
                  I’d like to explore my food practice with Lumen
                </label>
              </div>
            )}
          </div>
        )}

        {/* ── ACT 3: Your Supplement Protocol ── */}

        <div style={{ ...s.sectionLabel, marginBottom: '6px' }}>
          <span style={s.sectionLabelLine} />
          Your Supplement Protocol
        </div>

        {/* Budget banner — appears BEFORE product cards for minimal budget users */}
        {(assessmentAnswers?.q26_budget || '').startsWith('Minimal') && (
          <div style={s.budgetBanner}>
            <div style={s.budgetBannerEyebrow}>Where to start</div>
            <div style={s.budgetBannerTitle}>Meal Shakes + Vita-Lea — where it started</div>
            <p style={s.budgetBannerBody}>
              These two products are the original Shaklee foundation — complete protein and a
              full-spectrum multivitamin. At Member pricing, Meal Shakes runs about $30 per container
              (16 servings) and Vita-Lea about $13/month. Daily use lands around $43–60/month
              depending on shake frequency. For anyone starting from nothing, this combination
              produces a noticeable difference.
            </p>
            <p style={s.budgetBannerPhase}>
              The complete Foundation below is your roadmap — add OmegaGuard, Vita-D3, and
              Optiflora as budget allows. LifeShake is the upgraded protein step when you’re
              ready. The targeted modules are Phase 2. Lumen can help you sequence all of it.
            </p>
          </div>
        )}

        {/* Foundation products */}
        {foundational.length > 0 && (
          <div style={s.tierSection}>
            {(assessmentAnswers?.q26_budget || '').startsWith('Minimal') ? (
              <>
                {/* Start Here group — Meal Shakes + Vita-Lea */}
                {(() => {
                  const startHere = foundational.filter(p =>
                    p.id === 'meal-shakes' ||
                    p.name?.toLowerCase().includes('meal shake') ||
                    (p.item && String(p.item) === '20321') ||
                    p.name?.toLowerCase().includes('vita-lea') ||
                    p.id === 'vita-lea-womens' || p.id === 'vita-lea-mens' || p.id === 'vita-lea-mens-multi' ||
                    (p.item && ['20286','20631','20630','20285','20284'].includes(String(p.item)))
                  );
                  const roadmap = foundational.filter(p => !startHere.includes(p));
                  return (
                    <>
                      {startHere.length > 0 && (
                        <div style={{ marginBottom: '32px' }}>
                          <div style={s.tierHeader}>
                            <span style={s.tierIcon}>★</span>
                            <span style={s.tierLabel}>Start Here</span>
                            <span style={s.tierCount}>{startHere.length} products</span>
                          </div>
                          <p style={s.tierRationale}>
                            These two products are your foundation. Start with both, use them consistently,
                            and build from there when budget allows.
                          </p>
                          {startHere.map(p => (
                            <div key={p.name} style={{ border: '2px solid var(--amber)', borderRadius: '12px', marginBottom: '12px', overflow: 'hidden' }}>
                              <ProductCard product={p} />
                            </div>
                          ))}
                        </div>
                      )}
                      {roadmap.length > 0 && (
                        <div>
                          <div style={s.tierHeader}>
                            <span style={s.tierIcon}>🏛</span>
                            <span style={s.tierLabel}>Add Next</span>
                            <span style={s.tierCount}>{roadmap.length} products</span>
                          </div>
                          <p style={s.tierRationale}>
                            These are the products that will make a real difference as you build.
                            Each one was selected because of something specific your assessment identified.
                            Add them one at a time — start with whatever maps to your highest priority goal.
                          </p>
                          {roadmap.map(p => <ProductCard key={p.name} product={p} />)}
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            ) : (
              <>
                <div style={s.tierHeader}>
                  <span style={s.tierIcon}>🏛</span>
                  <span style={s.tierLabel}>Foundation</span>
                  <span style={s.tierCount}>{foundational.length} products</span>
                </div>
                <p style={s.tierRationale}>
                  These are your non-negotiables — the core daily support that applies regardless of your specific goals.
                  Foundation products address the nutritional gaps most likely to matter for your age, sex, dietary pattern,
                  and lifestyle. Start here before adding anything targeted.
                </p>
                {foundational.map(p => <ProductCard key={p.name} product={p} />)}
              </>
            )}
          </div>
        )}

        {/* ── Targeted Modules ── */}
        {modules.length > 0 && (
          <div style={s.tierSection}>
            <div style={{ ...s.sectionLabel, marginBottom: '6px' }}>
              <span style={s.sectionLabelLine} />
              Targeted Recommendations
            </div>
            <h2 style={s.sectionTitle}>Goal-specific additions</h2>
            <p style={s.sectionDesc}>
              {(assessmentAnswers?.q26_budget || '').startsWith('Minimal')
                ? "Phase 2 — these modules were activated by your specific priorities. Each addresses something your profile indicates matters to you. Add them after Foundation is established, one at a time, starting with whatever maps to your top priority."
                : (assessmentAnswers?.q26_budget || '').startsWith('Moderate')
                  ? "These modules were activated by your specific priorities and assessment inputs. Add them after Foundation is established, one module at a time — start with whichever maps to your highest priority goal."
                  : "These modules were activated by your specific priorities and assessment inputs. Each addresses something your profile indicates matters to you — the science behind each recommendation is inside the card. Add these after Foundation is established, one module at a time."}
            </p>
            {modules.map(m => <ModuleSection key={m.id} module={m} />)}
          </div>
        )}

        {/* ── Bob + Lumen Introduction ── */}
        <div style={{ borderTop: '1px solid var(--border)', padding: '72px 0 64px', background: 'var(--cream)' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 40px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ display: 'block', width: '32px', height: '1px', background: 'var(--amber)' }} />About this tool
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 2.5vw, 2.2rem)', fontWeight: 600, color: 'var(--navy)', marginBottom: '40px', lineHeight: 1.15 }}>
              Built from a life, not a brochure
            </h2>

            {/* Row 1: garden photo + opening copy */}
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '40px', alignItems: 'start', marginBottom: '40px' }}>
              <img src="/images/bob-garden.jpg" alt="Bob Ferguson" style={{ width: '200px', height: '220px', objectFit: 'cover', borderRadius: '6px' }} />
              <div>
                <p style={{ fontSize: '0.98rem', fontWeight: 300, lineHeight: 1.8, color: '#374151', margin: '0 0 1rem' }}>
                  Healthy, happy, high-functioning people tend to share a recognizable shape to their lives: physical practice, inner development, creative pursuit, meaningful work, and genuine connection. The specifics vary — yours might be pickleball where mine is rowing, journaling where mine is TM — but the pattern is consistent.
                </p>
                <p style={{ fontSize: '0.98rem', fontWeight: 300, lineHeight: 1.8, color: '#374151', margin: 0 }}>
                  Shaklee has been part of my version of that pattern for 49 years, since I joined my mother’s growing business at 27. The Hub is what living inside that framework for a lifetime looks like when you decide to share it.
                </p>
              </div>
            </div>

            {/* Row 2: copy + Freya photo */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '40px', alignItems: 'start', marginBottom: '40px' }}>
              <p style={{ fontSize: '0.98rem', fontWeight: 300, lineHeight: 1.8, color: '#374151', margin: 0 }}>
                At 75, I run two businesses, serve as an at-large city council member, train for competitive rowing, practice classical guitar, and tend a garden — alongside decades of TM practice and work as a Positive Intelligence coach. None of this is unusual for people who have invested in their whole life, not just their career. It’s a pattern. The Hub is built around it.
              </p>
              <img src="/images/bob-freya.jpg" alt="Bob with granddaughter Freya" style={{ width: '200px', height: '220px', objectFit: 'cover', borderRadius: '6px' }} />
            </div>

            {/* Row 3: award photo + Lumen card */}
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '40px', alignItems: 'start' }}>
              <img src="/images/bob-award.jpg" alt="Bob Ferguson at Shaklee Global Conference" style={{ width: '200px', height: '220px', objectFit: 'cover', objectPosition: 'top', borderRadius: '6px' }} />
              <div>
                <p style={{ fontSize: '0.98rem', fontWeight: 300, lineHeight: 1.8, color: '#374151', margin: '0 0 1.25rem' }}>
                  Lumen — the coaching intelligence inside this tool — is something I built with Claude.ai. Genuinely smart, deeply informed by the science behind your protocol, and not quite human. Spend time with it. But know that I’m here too.
                </p>
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: '8px' }}>Connect with Bob directly</div>
                  <p style={{ fontSize: '0.88rem', fontWeight: 300, lineHeight: 1.7, color: '#6B7280', margin: '0 0 14px' }}>
                    Whether you want to talk through your protocol with a real person, or just want a human in the loop — reach out.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <a href="mailto:bob@fergleads.com" style={{ fontSize: '0.82rem', padding: '6px 16px', borderRadius: '20px', border: '1px solid var(--border)', color: '#374151', textDecoration: 'none' }}>Email Bob</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ACT 4: Coaching ── */}
        {/* ── Coach CTA ── */}
        {(() => {
          const voice = psychograph?.voice || 'PRAGMATIST';
          const seededQuestions = {
            ANALYST: [
              'How does the Ghanim study actually apply to someone with my specific profile?',
              'Walk me through why these products were selected over alternatives.',
            ],
            SEEKER: [
              "I'm feeling a little overwhelmed — where should I actually start?",
              'How do these recommendations connect to what I described in my assessment?',
            ],
            PRAGMATIST: [
              "What's the fastest way to build a routine around this?",
              'Which of these should I start with first, and what do I add over time?',
            ],
          }[voice] || [];

          return (
            <div style={s.engageCard}>
              <div style={s.engageEyebrow}>
                <span style={s.engageEyebrowLine} />
                Lumen has read your full protocol
              </div>
              <div style={s.engageTitle}>Your protocol is ready. And Lumen has customized answers for you.</div>
              <p style={s.engageDesc}>
                Ask about any recommendation, build your daily routine, or figure out where to start first.
                Here are a few answers that Lumen has already thought about specifically for you.
              </p>
              <div style={s.engageQuestions}>
                {seededQuestions.map((q, i) => (
                  <Link key={i} to="/coach" state={{ dietOptIn, movementOptIn, openOptIn, dietSuggestionIds: dietSuggestions.map(sg => sg.id), movementSuggestionIds: movementSuggestions.map(sg => sg.id) }} style={s.engageQuestion}>
                    <span style={s.engageQuestionArrow}>→</span>
                    {q}
                  </Link>
                ))}
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '20px', marginTop: '4px' }}>
                <label style={{ ...s.practiceCheckbox, borderTop: 'none', paddingTop: '0', marginTop: '0' }}>
                  <input
                    type="checkbox"
                    checked={openOptIn}
                    onChange={e => setOpenOptIn(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: 'var(--amber)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 300 }}>
                    There’s something beyond habits, food, and supplements I want to bring to Lumen
                  </span>
                </label>
              </div>
              <Link to="/coach" state={{ dietOptIn, movementOptIn, openOptIn, dietSuggestionIds: dietSuggestions.map(sg => sg.id), movementSuggestionIds: movementSuggestions.map(sg => sg.id) }}>
                <button style={{ ...s.engageBtn, marginTop: '16px' }}>Start with Lumen →</button>
              </Link>
            </div>
          );
        })()}

        {/* ── Compliance ── */}
        <div style={s.compliance}>
          <strong>Important.</strong> These statements have not been evaluated by the Food and Drug
          Administration. Products listed in this protocol are not intended to diagnose, treat, cure,
          or prevent any disease. This tool is authored and operated by an independent Shaklee
          distributor and is not affiliated with or authorized by Shaklee Corporation.
          Always consult a qualified healthcare professional before beginning any supplement program,
          particularly if you are pregnant, nursing, taking medications, or managing a health condition.
        </div>

      </div>
    </div>
  );
}
